import React, { useContext, useEffect, useRef, useState } from "react";
import { Home, Briefcase, MapPin, ArrowLeft } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

import Navbar from "../../LandingPage/Navbar";
import Footer from "../../LandingPage/Footer";
import { GlobalContext } from "../../../Context/GlobalContext";
import useCustomerDetails from "../../Services/Customer/useCustomerDetails";
import useGoogleLocationApi from "../../Services/Customer/useGoogleLocationApi";
import AddressForm from "./AddressForm";
import AddressList from "./AddressList";

const Address = () => {
  const navigate = useNavigate();
  const searchTimerRef = useRef(null);

  const { userCode, latitude, longitude } = useContext(GlobalContext);

  const {
    updateFavouriteAddressApi,
    getFavouriteAddressApi,
    updateFavouriteAddressLoading,
    getFavouriteAddressLoading,
  } = useCustomerDetails();

  const { placesAutoCompletionApi, placesAutoCompletionLoading } =
    useGoogleLocationApi();

  const [addresses, setAddresses] = useState([]);
  const [selectedFavouriteKey, setSelectedFavouriteKey] = useState("");
  const [savingFavouriteIndex, setSavingFavouriteIndex] = useState(null);

  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [resolvingLatLong, setResolvingLatLong] = useState(false);

  const [formData, setFormData] = useState({
    address: "",
    latitude: "",
    longitude: "",
    addressType: "Home",
    isFavourite: false,
  });

  const extractAddressList = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.addresses ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;
    if (Array.isArray(data?.addresses)) return data.addresses;
    if (data && typeof data === "object") return [data];

    return [];
  };

  const extractPlaceSuggestions = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.predictions ||
      apiResponse?.suggestions ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.predictions)) return data.predictions;
    if (Array.isArray(data?.suggestions)) return data.suggestions;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;

    return [];
  };

  const normalizeSuggestion = (item) => {
    const address =
      item?.description ||
      item?.formatted_address ||
      item?.address ||
      item?.name ||
      item?.main_text ||
      item?.structured_formatting?.main_text ||
      item?.terms?.map((term) => term?.value).join(", ") ||
      "";

    const secondaryText =
      item?.secondary_text ||
      item?.structured_formatting?.secondary_text ||
      item?.subtitle ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location?.lat ||
      item?.location?.latitude ||
      item?.geometry?.location?.lat ||
      item?.geometry?.location?.latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.location?.lng ||
      item?.location?.longitude ||
      item?.geometry?.location?.lng ||
      item?.geometry?.location?.longitude ||
      "";

    return {
      ...item,
      displayAddress: address,
      secondaryText,
      latitude: lat,
      longitude: lng,
      placeId: item?.place_id || item?.placeId || "",
    };
  };

  const cleanAddressTypeFromAddress = (address) => {
    return String(address || "").replace(/\s*\((Home|Office|Other)\)\s*$/i, "");
  };

  const getAddressType = (item) => {
    const directType =
      item?.addressType ||
      item?.address_type ||
      item?.type ||
      item?.favouriteName ||
      item?.favourite ||
      "";

    if (directType) return directType;

    const address = item?.address || item?.fullAddress || item?.location || "";

    const match = String(address).match(/\((Home|Office|Other)\)\s*$/i);

    if (match?.[1]) return match[1];

    return "Address";
  };

  const getAddressKey = (item) => {
    const address = item?.address || item?.fullAddress || item?.location || "";
    const lat =
      item?.latitude ||
      item?.lat ||
      item?.addressLatitude ||
      item?.address_latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.addressLongitude ||
      item?.address_longitude ||
      "";

    return `${address}-${lat}-${lng}`;
  };

  const getLatLongFromAddressText = async (addressText) => {
    if (!addressText) return null;

    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      toast.error("Google Maps API key missing in .env");
      return null;
    }

    try {
      setResolvingLatLong(true);

      const searchQuery = `${addressText}, India`;

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          searchQuery,
        )}&key=${googleKey}`,
      );

      const data = await response.json();

      if (data?.status === "OK" && data?.results?.length > 0) {
        const selectedPlace = data.results[0];

        const lat = selectedPlace?.geometry?.location?.lat;
        const lng = selectedPlace?.geometry?.location?.lng;

        if (!lat || !lng) {
          toast.error("Latitude/longitude not found for this address");
          return null;
        }

        return {
          address: selectedPlace?.formatted_address || addressText,
          latitude: String(lat),
          longitude: String(lng),
        };
      }

      if (data?.status === "ZERO_RESULTS") {
        toast.error(
          "Location not found. Please enter a more complete address.",
        );
      } else if (data?.status === "REQUEST_DENIED") {
        toast.error("Google Maps request denied. Check API key permissions.");
      } else {
        toast.error(data?.error_message || "Unable to get latitude/longitude");
      }

      return null;
    } catch (error) {
      console.error("Geocode fallback error:", error);
      toast.error("Unable to get latitude/longitude");
      return null;
    } finally {
      setResolvingLatLong(false);
    }
  };

  const fetchFavouriteAddresses = async () => {
    if (!userCode) return;

    const result = await getFavouriteAddressApi({
      userId: userCode,
    });

    const list = extractAddressList(result);

    setAddresses(list);

    const savedFavouriteKey = localStorage.getItem(
      `selected_favourite_address_${userCode}`,
    );

    if (savedFavouriteKey) {
      setSelectedFavouriteKey(savedFavouriteKey);
      return;
    }

    if (list.length > 0) {
      const firstAddressKey = getAddressKey(list[0]);

      setSelectedFavouriteKey(firstAddressKey);
      localStorage.setItem(
        `selected_favourite_address_${userCode}`,
        firstAddressKey,
      );
    }
  };

  useEffect(() => {
    fetchFavouriteAddresses();

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [userCode]);

  const handleUseCurrentLocation = () => {
    if (!latitude || !longitude) {
      toast.error("Current location not available");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      latitude: String(latitude),
      longitude: String(longitude),
    }));

    toast.success("Current location added");
  };

  const handleSearchPlaces = async (value) => {
    if (!value || value.trim().length < 3) {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const result = await placesAutoCompletionApi({
      input: value,
      latitude: Number(latitude || 0),
      longitude: Number(longitude || 0),
      radius: 50000,
    });

    const suggestions =
      extractPlaceSuggestions(result).map(normalizeSuggestion);

    setPlaceSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const handleAddressInputChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      address: value,
      latitude: "",
      longitude: "",
    }));

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      handleSearchPlaces(value);
    }, 450);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const selected = normalizeSuggestion(suggestion);

    const selectedAddress = selected.displayAddress || formData.address;

    setShowSuggestions(false);
    setPlaceSuggestions([]);

    if (selected.latitude && selected.longitude) {
      setFormData((prev) => ({
        ...prev,
        address: selectedAddress,
        latitude: String(selected.latitude),
        longitude: String(selected.longitude),
      }));

      toast.success("Address selected");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      address: selectedAddress,
      latitude: "",
      longitude: "",
    }));

    const resolvedLocation = await getLatLongFromAddressText(selectedAddress);

    if (resolvedLocation) {
      setFormData((prev) => ({
        ...prev,
        address: resolvedLocation.address,
        latitude: resolvedLocation.latitude,
        longitude: resolvedLocation.longitude,
      }));

      toast.success("Address and location selected");
    } else {
      toast.error("Lat/long not found. Please select another address.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userCode) {
      toast.error("User not found. Please login again.");
      return;
    }

    if (!formData.address.trim()) {
      toast.error("Please enter address");
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error("Please select an address that has latitude and longitude");
      return;
    }

    const finalAddress = cleanAddressTypeFromAddress(formData.address);

    const result = await updateFavouriteAddressApi({
      userId: userCode,
      latitude: String(formData.latitude),
      longitude: String(formData.longitude),
      address: finalAddress,
      favourite: formData.addressType, // Home / Office / Other
    });

    if (result?.responseCode === "00" || result?.status === true || result) {
      toast.success(result?.responseMessage || "Address saved successfully");

      const newAddressItem = {
        address: finalAddress,
        latitude: String(formData.latitude),
        longitude: String(formData.longitude),
        favourite: formData.addressType,
        addressType: formData.addressType,
      };

      const newAddressKey = getAddressKey(newAddressItem);

      /**
       * First address auto favourite
       */
      if (addresses.length === 0) {
        setSelectedFavouriteKey(newAddressKey);
        localStorage.setItem(
          `selected_favourite_address_${userCode}`,
          newAddressKey,
        );
      }

      setFormData({
        address: "",
        latitude: "",
        longitude: "",
        addressType: "Home",
        isFavourite: false,
      });

      setPlaceSuggestions([]);
      setShowSuggestions(false);

      fetchFavouriteAddresses();
    }
  };

  const handleSelectSavedFavourite = (item, index) => {
    const address = item?.address || item?.fullAddress || item?.location || "";
    const lat =
      item?.latitude ||
      item?.lat ||
      item?.addressLatitude ||
      item?.address_latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.addressLongitude ||
      item?.address_longitude ||
      "";

    if (!address || !lat || !lng) {
      toast.error("This address does not have complete location data");
      return;
    }

    const addressKey = getAddressKey(item);

    setSelectedFavouriteKey(addressKey);
    localStorage.setItem(`selected_favourite_address_${userCode}`, addressKey);

    toast.success("Favourite address selected");
  };

  const getAddressIcon = (type) => {
    const value = String(type || "").toLowerCase();

    if (value.includes("home")) return Home;
    if (value.includes("work") || value.includes("office")) return Briefcase;

    return MapPin;
  };

  return (
    <>
      <Navbar />
      <ToastContainer />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-100 px-4 pb-12 pt-28">
        <div className="mx-auto max-w-[1120px]">
          <button
            onClick={() => navigate(-1)}
            className="mb-5 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100"
          >
            <ArrowLeft size={17} />
            Back
          </button>

          <div className="mb-8 overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-6 text-white shadow-[0_18px_50px_rgba(37,99,235,0.25)] md:p-8">
            <div className="relative">
              <div className="absolute right-[-70px] top-[-90px] h-52 w-52 rounded-full bg-white/10" />
              <div className="absolute bottom-[-100px] left-[-80px] h-56 w-56 rounded-full bg-white/10" />

              <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                    <MapPin size={30} />
                  </div>

                  <h1 className="text-2xl font-bold md:text-4xl">
                    Saved Addresses
                  </h1>

                  <p className="mt-2 max-w-[620px] text-sm text-white/75 md:text-base">
                    Search nearby addresses, save them as Home, Office, or
                    Other, and click any saved address to make it favourite.
                  </p>
                </div>

                <div className="rounded-3xl bg-white/15 px-5 py-4 backdrop-blur">
                  <p className="text-sm text-white/70">Total Addresses</p>
                  <h2 className="mt-1 text-3xl font-bold">
                    {addresses?.length || 0}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
            {/* FORM */}
            <AddressForm
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              handleUseCurrentLocation={handleUseCurrentLocation}
              handleAddressInputChange={handleAddressInputChange}
              handleSelectSuggestion={handleSelectSuggestion}
              placeSuggestions={placeSuggestions}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              placesAutoCompletionLoading={placesAutoCompletionLoading}
              resolvingLatLong={resolvingLatLong}
              updateFavouriteAddressLoading={updateFavouriteAddressLoading}
              normalizeSuggestion={normalizeSuggestion}
            />

            {/* LIST */}
            <AddressList
              addresses={addresses}
              selectedFavouriteKey={selectedFavouriteKey}
              savingFavouriteIndex={savingFavouriteIndex}
              getFavouriteAddressLoading={getFavouriteAddressLoading}
              getAddressKey={getAddressKey}
              getAddressType={getAddressType}
              getAddressIcon={getAddressIcon}
              cleanAddressTypeFromAddress={cleanAddressTypeFromAddress}
              handleSelectSavedFavourite={handleSelectSavedFavourite}
            />
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Address;
